<template>
  <tr
    class="vst-row"
    :class="[itemClass(item.data), rowClass(item)]"
  >
    <td
      v-for="(h, idx) in headers"
      :key="h.value"
      class="vst-row-cell"
      :class="[
        h.class,
        h.align && `text-${h.align}`,
        { 'vst-sticky': idx < realFixedColsCount, 'vst-cell-truncate': hasMaxWidth(h) },
        rowBgCellClass(item, idx),
      ]"
      :style="idx < realFixedColsCount ? { left: (idx < fixedColsCount ? realStickyOffsets[idx] : stickyOffsets[idx]) + 'px' } : {}"
    >
      <div class="vst-cell-inner" :class="{ 'vst-cell-inner--deep-toggle': isDeepToggleCol(item.level, idx) }">
        <v-icon
          v-if="isDeepToggleCol(item.level, idx)"
          color="#AEAEAE"
          size="16"
          class="vst-deep-toggle flex-shrink-0"
          :class="item.level ? 'ml-auto' : 'mr-2'"
          style="margin-top: -2px"
          @click="$emit('toggle-deep', item, isFullyExpanded(item))"
        >
          {{ isFullyExpanded(item) ? 'mdi-chevron-double-up' : 'mdi-chevron-double-down' }}
        </v-icon>
        <v-icon
          v-if="item.hasChildren && isGroupIndicatorCol(item.level, idx)"
          color="#AEAEAE"
          size="16"
          class="mr-1 flex-shrink-0"
          style="margin-top: -2px"
          @click="$emit('toggle', item)"
        >
          {{ expanded[item.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
        <span v-if="item.data._fullTextField === h.value" class="vst-full-text">{{ getValue(item.data, h.value) }}</span>
        <slot
          v-else-if="$scopedSlots[`${slotLevelTitle[item.level]}.${h.value}`]"
          :name="`${slotLevelTitle[item.level]}.${h.value}`"
          :item="item.data"
          :parent="item.data?._parent"
          :value="item.data[h.value]"
          :header="h"
        >
          {{ item.data[h.value] }}
        </slot>
        <slot v-else :name="`item.${h.value}`" :item="item.data" :value="getValue(item.data, h.value)" :level="item.level">
          {{ getValue(item.data, h.value) }}
        </slot>
      </div>
    </td>
  </tr>
</template>

<script>
import get from 'lodash/get'
export default {
  name: 'VSTRootRow',
  props: {
    item: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    realFixedColsCount: {
      type: Number,
      required: true,
    },
    fixedColsCount: {
      type: Number,
      required: true,
    },
    realStickyOffsets: {
      type: Array,
      required: true,
    },
    stickyOffsets: {
      type: Array,
      required: true,
    },
    expanded: {
      type: Object,
      required: true,
    },
    slotLevelTitle: {
      type: Object,
      required: true,
    },
    itemClass: {
      type: Function,
      default: () => '',
    },
    valueMutator: {
      type: Function,
      default: () => null,
    },
  },
  methods: {
    groupStartIdx(level) {
      const explicitIdx = this.headers.findIndex(h => h.groupLevel === level)
      return explicitIdx !== -1 ? explicitIdx : level
    },
    isGroupIndicatorCol(level, idx) {
      return idx === this.groupStartIdx(level)
    },
    // Double arrow: only rows with grandchildren (the last two levels have nothing to expand deeply).
    // Root level — in the same cell before the single arrow, nested levels — at the right edge of the previous column
    isDeepToggleCol(level, idx) {
      if (!this.item.children.some(child => child.hasChildren)) return false
      return level ? idx === this.groupStartIdx(level) - 1 : idx === this.groupStartIdx(level)
    },
    isFullyExpanded(row) {
      if (!this.expanded[row.id]) return false
      return row.children.every(child => !child.hasChildren || this.isFullyExpanded(child))
    },
    rowClass(item) {
      const rowBgClasses = {
        1: 'multirow__second-row',
        2: 'multirow__third-row',
        3: 'multirow__fourth-row',
        4: 'multirow__fifth-row',
      }
      return item.level ? rowBgClasses[item.level] : ''
    },
    rowBgCellClass(item, idx) {
      if (!item.level) return ''
      const rowBgCellClasses = {
        1: 'multirow__second-row-cell',
        2: 'multirow__third-row-cell',
        3: 'multirow__fourth-row-cell',
        4: 'multirow__fifth-row-cell',
      }
      return idx >= this.groupStartIdx(item.level) ? rowBgCellClasses[item.level] : ''
    },
    hasMaxWidth(h) {
      const cls = h.class || ''
      const str = Array.isArray(cls) ? cls.join(' ') : String(cls)
      return str.includes('max-width-')
    },
    getValue(item, key) {
      const value = get(item, key)
      if (this.valueMutator) {
        return this.valueMutator({
          value,
          item,
          key,
        })
      }
      return value
    },
  },
}
</script>

<style lang="scss">
td.vst-row-cell.vst-cell-truncate {
  text-overflow: ellipsis;

  > .vst-cell-inner {
    display: flex;
    align-items: center;
    overflow: hidden;

    > span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    > a {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;

      > span {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.vst-cell-inner--deep-toggle {
  display: flex;
  align-items: center;
}

.vst-full-text {
  overflow: visible !important;
  text-overflow: unset !important;
  white-space: nowrap;
  min-width: auto !important;
}
</style>
<style lang="scss" scoped>
.vst-row {
  background-color: #ffffff;
  border-bottom: 1px solid #eee;
  td.vst-row-cell {
    background-color: inherit;
    color: #343434;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    height: 40px !important;
    padding: 0 8px;
    white-space: nowrap;
    overflow: hidden;
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;

    &.vst-sticky {
      position: sticky;
      z-index: 2;
    }
  }
}

.multirow__second-row > td.multirow__second-row-cell {
  background-color: var(--v-violet50-base) !important;
  border-bottom: 1px solid var(--v-violet200-base) !important;
}
.multirow__third-row > td.multirow__third-row-cell {
  background-color: var(--v-teal50-base) !important;
  border-bottom: 1px solid var(--v-teal200-base) !important;
}
.multirow__fourth-row > td.multirow__fourth-row-cell {
  background-color: var(--v-orange50-base) !important;
  border-bottom: 1px solid var(--v-orange200-base) !important;
}
.multirow__fifth-row > td.multirow__fifth-row-cell {
  background-color: var(--v-yellow50-base) !important;
  border-bottom: 1px solid var(--v-yellow200-base) !important;
}
</style>
